import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRecentlyViewed, addToRecentlyViewed } from '../utils/recentlyViewed';
import { FaHeart, FaEye, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useWishlist } from '../context/WishlistContext';
import toast from 'react-hot-toast';

// Create a placeholder image data URL to avoid network calls
const PLACEHOLDER_IMAGE_DATA_URL = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCA4MEgxMjBWMTIwSDgwVjgwWiIgZmlsbD0iI0Q1RDlERCIvPgo8L3N2Zz4K';

// Custom hook for image loading with error handling
const useImageLoader = (src) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!src) {
      setImageSrc(PLACEHOLDER_IMAGE_DATA_URL);
      setIsLoading(false);
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    const img = new Image();
    
    img.onload = () => {
      setImageSrc(src);
      setIsLoading(false);
      setHasError(false);
    };
    
    img.onerror = () => {
      setImageSrc(PLACEHOLDER_IMAGE_DATA_URL);
      setIsLoading(false);
      setHasError(true);
    };
    
    img.src = src;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src]);

  return { imageSrc, isLoading, hasError };
};

// Product image component with optimized loading
const ProductImage = ({ product }) => {
  const formatImage = (imageData) => {
    if (!imageData) return null;
    
    if (typeof imageData === 'string') {
      if (imageData.startsWith('http')) {
        return imageData;
      }
      
      // Handle comma-separated images
      const images = imageData.split(',');
      const firstImage = images[0].trim();
      return firstImage.startsWith('http') ? firstImage : `https://newpos.spectsgenie.com/${firstImage}`;
    }
    
    if (Array.isArray(imageData) && imageData.length > 0) {
      return imageData[0];
    }
    
    return null;
  };

  const imageUrl = formatImage(product.image);
  const { imageSrc, isLoading, hasError } = useImageLoader(imageUrl);

  return (
    <div className="relative aspect-square overflow-hidden rounded-t-lg">
      {isLoading && (
        <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
          <div className="w-6 h-6 bg-gray-300 rounded"></div>
        </div>
      )}
      
      <img
        src={imageSrc}
        alt={product.name}
        className={`w-full h-full object-cover transition-transform duration-300 ${
          isLoading ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ display: isLoading ? 'none' : 'block' }}
      />
      
    </div>
  );
};

const RecentlyViewed = ({ currentProductId }) => {
  const [recentProducts, setRecentProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const navigate = useNavigate();
  const carouselRef = useRef(null);
  const { isInWishlist, toggleWishlist, isLoggedIn } = useWishlist();

  // Get items per view based on screen size
  const getItemsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 640) return 2.2; // Mobile - show 2.2 items
      if (window.innerWidth < 768) return 3.2; // Small tablet - show 3.2 items
      if (window.innerWidth < 1024) return 4.2; // Tablet - show 4.2 items
      if (window.innerWidth < 1280) return 5.2; // Desktop - show 5.2 items
      return 6.2; // Large desktop - show 6.2 items
    }
    return 2.2;
  };

  const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

  useEffect(() => {
    const handleResize = () => {
      setItemsPerView(getItemsPerView());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadRecentlyViewed = () => {
      const products = getRecentlyViewed();
      // Filter out current product if viewing product details
      const filteredProducts = currentProductId 
        ? products.filter(product => product.id !== currentProductId)
        : products;
      setRecentProducts(filteredProducts);
      setLoading(false);
    };

    loadRecentlyViewed();
    
    // Listen for storage changes to update when new products are added
    const handleStorageChange = () => {
      loadRecentlyViewed();
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentProductId]);

  const handleProductClick = (product) => {
    // Add to recently viewed when clicking
    addToRecentlyViewed(product);
    
    // Navigate to product details
    navigate(`/product/${product.id}/${product.ca_id}`, {
      state: { product }
    });
  };

  const handleWishlistClick = async (e, product) => {
    e.stopPropagation();
    
    if (!isLoggedIn) {
      toast.error("Please login to add items to wishlist");
      return;
    }

    try {
      const productData = {
        product_id: product.id,
        ca_id: product.ca_id,
        name: product.name,
        price: product.salePrice || product.price,
        pr_image: product.image,
        slug: product.slug
      };

      const result = await toggleWishlist(productData);
      
      if (result.success) {
        toast.success(result.message, {
          duration: 2000,
          position: 'top-center',
          style: {
            background: '#10B981',
            color: '#fff',
          }
        });
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error("Error updating wishlist");
    }
  };

  const formatPrice = (price) => {
    if (!price) return '0';
    return typeof price === 'string' ? price : price.toString();
  };

  const handlePrevious = () => {
    if (isTransitioning || currentIndex === 0) return;
    
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.max(0, prev - 1));
    
    setTimeout(() => setIsTransitioning(false), 300);
  };

  const handleNext = () => {
    if (isTransitioning) return;
    
    const maxIndex = Math.max(0, recentProducts.length - Math.floor(itemsPerView));
    if (currentIndex >= maxIndex) return;
    
    setIsTransitioning(true);
    setCurrentIndex(prev => Math.min(maxIndex, prev + 1));
    
    setTimeout(() => setIsTransitioning(false), 300);
  };

  // Touch/swipe handling for mobile
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrevious();
    }
  };

  if (loading) {
    return (
      <div className="w-full">
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5].map(i => (
            <div 
              key={i} 
              className="flex-shrink-0 w-32 sm:w-40 md:w-44 lg:w-48"
              style={{ width: `${100 / itemsPerView}%` }}
            >
              <div className="bg-gray-200 rounded-lg animate-pulse">
                <div className="aspect-square bg-gray-300 rounded-t-lg"></div>
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-300 rounded"></div>
                  <div className="h-3 bg-gray-300 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (recentProducts.length === 0) {
    return (
      <div className="text-center py-8">
        <FaEye className="mx-auto text-4xl text-gray-400 mb-4" />
        <p className="text-gray-500 text-sm sm:text-base">No recently viewed products</p>
        <p className="text-xs sm:text-sm text-gray-400 mt-2">Products you view will appear here</p>
      </div>
    );
  }

  const maxIndex = Math.max(0, recentProducts.length - Math.floor(itemsPerView));
  const canGoPrevious = currentIndex > 0;
  const canGoNext = currentIndex < maxIndex;

  return (
    <div className="w-full relative">
      {/* Navigation Buttons - Hidden on mobile, visible on larger screens */}
      {recentProducts.length > Math.floor(itemsPerView) && (
        <>
          <button
            onClick={handlePrevious}
            disabled={!canGoPrevious || isTransitioning}
            className={`hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-8 h-8 bg-white rounded-full shadow-lg items-center justify-center transition-all duration-200 border border-gray-200 ${
              canGoPrevious && !isTransitioning
                ? 'hover:shadow-xl hover:scale-110 text-gray-700 cursor-pointer' 
                : 'text-gray-300 cursor-not-allowed opacity-50'
            }`}
          >
            <FaChevronLeft className="text-sm" />
          </button>

          <button
            onClick={handleNext}
            disabled={!canGoNext || isTransitioning}
            className={`hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-8 h-8 bg-white rounded-full shadow-lg items-center justify-center transition-all duration-200 border border-gray-200 ${
              canGoNext && !isTransitioning
                ? 'hover:shadow-xl hover:scale-110 text-gray-700 cursor-pointer' 
                : 'text-gray-300 cursor-not-allowed opacity-50'
            }`}
          >
            <FaChevronRight className="text-sm" />
          </button>
        </>
      )}

      {/* Carousel Container */}
      <div 
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={carouselRef}
          className="flex transition-transform duration-300 ease-out gap-3"
          style={{
            transform: `translateX(-${currentIndex * (100 / itemsPerView)}%)`,
          }}
        >
          {recentProducts.map((product) => {
            const isWishlisted = isInWishlist(product.id);
            
            return (
              <div
                key={product.id}
                className="flex-shrink-0 cursor-pointer group"
                style={{ width: `${100 / itemsPerView}%` }}
                onClick={() => handleProductClick(product)}
              >
                <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100 overflow-hidden h-full">
                  {/* Product Image */}
                  <ProductImage product={product} />

                  {/* Product Info */}
                  <div className="p-2 sm:p-3">
                    <h3 className="text-xs sm:text-sm font-semibold text-gray-800 mb-1 line-clamp-2 leading-tight min-h-[2rem] sm:min-h-[2.5rem]">
                      {product.name}
                    </h3>
                    
                    <div className="flex items-center gap-1 sm:gap-2 mb-1 sm:mb-2">
                      {product.salePrice && product.salePrice !== product.price ? (
                        <>
                          <span className="text-sm sm:text-lg font-bold text-[#00007a]">
                            ₹{formatPrice(product.price)}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-500 line-through">
                            ₹{formatPrice(product.salePrice)}
                          </span>
                        </>
                      ) : (
                        <span className="text-sm sm:text-lg font-bold text-[#00007a]">
                          ₹{formatPrice(product.salePrice || product.price)}
                        </span>
                      )}
                    </div>

                    {product.shape && (
                      <div className="text-xs text-gray-500 mb-1 sm:mb-2 truncate">
                        {product.shape}
                      </div>
                    )}

                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots Indicator - Only show on mobile when there are multiple pages */}
      {recentProducts.length > Math.floor(itemsPerView) && (
        <div className="flex sm:hidden justify-center mt-4 gap-1">
          {Array.from({ length: maxIndex + 1 }).map((_, index) => (
            <button
              key={index}
              onClick={() => {
                if (!isTransitioning) {
                  setIsTransitioning(true);
                  setCurrentIndex(index);
                  setTimeout(() => setIsTransitioning(false), 300);
                }
              }}
              className={`w-2 h-2 rounded-full transition-all duration-200 ${
                index === currentIndex 
                  ? 'bg-blue-500 w-4' 
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default RecentlyViewed;